/*
 This file is part of GNU Taler
 (C) 2022-2024 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */
import {
  Amounts,
  HttpStatusCode,
  TalerError,
  assertUnreachable,
} from "@gnu-taler/taler-util";
import { Loading, useTranslationContext } from "@gnu-taler/web-util/browser";
import { Fragment, VNode, h } from "preact";

import { RouteDefinition } from "@gnu-taler/web-util/browser";
import {
  revalidateAccountDetails,
  useAccountDetails,
} from "../hooks/account.js";
import { useSessionState } from "../hooks/session.js";
import { LoginForm } from "./LoginForm.js";
import { PaytoWireTransferForm } from "./PaytoWireTransferForm.js";
import { IntAmounts } from "./regional/CreateCashout.js";
import { RetryableError } from "../components/RetryableError.js";

export function WireTransfer({
  toAccount,
  withSubject,
  withAmount,

  routeCancel,
  onSuccess,
}: {
  onSuccess?: () => void;
  toAccount?: string;
  withSubject?: string;
  withAmount?: string;
  routeCancel?: RouteDefinition;
}): VNode {
  const { i18n } = useTranslationContext();
  const r = useSessionState();
  const account = r.state.status !== "loggedOut" ? r.state.username : "admin";
  const result = useAccountDetails(account);

  if (!result) {
    return <Loading />;
  }
  if (result instanceof TalerError) {
    return (
      <Fragment>
        <RetryableError
          error={result}
          title={i18n.str`Failed to load account details.`}
          onRetry={() => void revalidateAccountDetails()}
        />
        <LoginForm currentUser={account} />
      </Fragment>
    );
  }
  if (result.type === "fail") {
    switch (result.case) {
      case HttpStatusCode.Unauthorized:
        return <LoginForm currentUser={account} />;
      case HttpStatusCode.NotFound:
        return <LoginForm currentUser={account} />;
      default:
        assertUnreachable(result);
    }
  }
  const { body: data } = result;

  const balanceAbs = Amounts.parseOrThrow(data.balance.amount);
  const isBalanceNegative = data.balance.credit_debit_indicator == "debit";
  const debitThreshold = Amounts.parseOrThrow(data.debit_threshold);

  const balance = IntAmounts.toIntAmount(balanceAbs, isBalanceNegative);
  const limit = balance.increment(debitThreshold).result;

  return (
    <section class="mt-2">
      {routeCancel ? (
        <a
          href={routeCancel.url({})}
          class="inline-flex items-center text-sm font-semibold text-brand hover:underline"
        >
          <span class="mr-1" aria-hidden="true">
            ←
          </span>
          <i18n.Translate>Back to overview</i18n.Translate>
        </a>
      ) : undefined}
      <div class="mx-auto mt-6 max-w-3xl">
        <h1 class="text-2xl font-semibold text-brand">
          <i18n.Translate>Send bank transfer</i18n.Translate>
        </h1>
        <p class="mt-2 text-sm text-gray-600">
          <i18n.Translate>
            Send money from this account to another bank account.
          </i18n.Translate>
        </p>
        <PaytoWireTransferForm
          withAccount={toAccount}
          withAmount={withAmount}
          withSubject={withSubject}
          limit={limit}
          debtLimit={debitThreshold}
          onSuccess={onSuccess}
          routeCancel={routeCancel}
        />
      </div>
    </section>
  );
}
